/**
 * Request Deduplication Utility
 *
 * Prevents duplicate API requests from being made simultaneously.
 * Useful when multiple components might trigger the same API call.
 */

/**
 * Map to store pending requests by their unique key
 */
const pendingRequests = new Map<string, Promise<unknown>>();

/**
 * Executes a request function and deduplicates concurrent calls.
 * If the same request is already in progress, returns the existing promise.
 *
 * @param key - Unique identifier for the request (e.g., 'users:list' or 'user:123')
 * @param requestFn - Function that returns a promise for the request
 * @returns Promise resolving to the request result
 *
 * @example
 * // Multiple components calling this will only make one API call
 * const users = await deduplicatedRequest(
 *   'users:list',
 *   () => apiClient.get('/users')
 * );
 */
export async function deduplicatedRequest<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
  // 1. Instant check for existing in-flight promise
  const existingPromise = pendingRequests.get(key);
  if (existingPromise) {
    return existingPromise as Promise<T>;
  }

  // 2. Wrap the execution and register it IMMEDIATELY
  // DESIGN RATIONALE: By creating the promise synchronously and setting it,
  // we block subsequent calls in the same tick from initiating a new request.
  const promise = (async () => {
    try {
      return await requestFn();
    } finally {
      // Automatic cleanup to ensure memory safety
      pendingRequests.delete(key);
    }
  })();

  pendingRequests.set(key, promise);

  return promise;
}

/**
 * Clears a specific pending request if you need to force a refresh.
 *
 * @param key - The request key to clear
 */
export function clearPendingRequest(key: string): void {
  pendingRequests.delete(key);
}

/**
 * Clears all pending requests.
 * Useful when logging out or navigating away from a feature.
 */
export function clearAllPendingRequests(): void {
  pendingRequests.clear();
}

/**
 * Checks if a request with the given key is currently pending.
 *
 * @param key - The request key to check
 * @returns true if the request is in progress
 */
export function isPending(key: string): boolean {
  return pendingRequests.has(key);
}
